/**
 * @description
 * React hook for fetching and managing deployment readiness state.
 * Provides loading states, error handling, and refetch capability.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 10, 2025
 * @see admin/src/frontend/services/api.ts
 */

import { useState, useEffect, useCallback } from "react";
import { api } from "../services/api";

/**
 * Deployment readiness data structure
 */
export interface DeploymentReadiness {
  isReady: boolean;
  readinessScore: number;
  missingRequired: string[];
  missingOptional: string[];
  recommendations: string[];
  details: {
    aiProviders: {
      isValid: boolean;
      configuredProviders: string[];
      missingProviders: string[];
      warnings: string[];
    };
    oauth: {
      isValid: boolean;
      googleOAuth: boolean;
      githubOAuth: boolean;
      githubExporter: boolean;
      warnings: string[];
    };
    cloudflare: {
      isValid: boolean;
      hasApiToken: boolean;
      hasAccountId: boolean;
      warnings: string[];
    };
  };
}

/**
 * Hook return type
 */
export interface UseDeploymentReadinessReturn {
  readiness: DeploymentReadiness | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch deployment readiness for a client
 * @param clientId The client ID to check readiness for
 * @returns Readiness data, loading state, error, and refetch function
 */
export function useDeploymentReadiness(
  clientId?: string,
): UseDeploymentReadinessReturn {
  const [readiness, setReadiness] = useState<DeploymentReadiness | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReadiness = useCallback(async () => {
    if (!clientId) {
      setReadiness(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await api.getDeploymentReadiness(clientId);
      setReadiness(data.readiness);
    } catch (err) {
      // Don't log 404s as errors - endpoint might not be fully configured
      if (err instanceof Error && err.message.includes('404')) {
        setReadiness(null);
        setLoading(false);
        return;
      }
      
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to fetch deployment readiness";
      setError(errorMessage);
      console.error("Error fetching deployment readiness:", err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  return {
    readiness,
    loading,
    error,
    refetch: loadReadiness,
  };
}

/**
 * Custom hook to fetch deployment readiness for multiple clients
 * @param clientIds Array of client IDs to check readiness for
 * @returns Map of client ID to readiness data, loading state, error, and refetch function
 */
export function useDeploymentReadinessMap(
  clientIds: string[],
): {
  readinessMap: Map<string, DeploymentReadiness>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [readinessMap, setReadinessMap] = useState<Map<string, DeploymentReadiness>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReadinessMap = useCallback(async () => {
    if (!clientIds.length) {
      setReadinessMap(new Map());
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newMap = new Map<string, DeploymentReadiness>();
      
      // Load readiness for each client in parallel
      const promises = clientIds.map(async (clientId) => {
        try {
          const data = await api.getDeploymentReadiness(clientId);
          newMap.set(clientId, data.readiness);
        } catch (err) {
          // Silently skip 404s - endpoint might not be fully configured
          if (err instanceof Error && !err.message.includes('404')) {
            console.error(`Error fetching readiness for client ${clientId}:`, err);
          }
        }
      });

      await Promise.all(promises);
      setReadinessMap(newMap);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to fetch deployment readiness map";
      setError(errorMessage);
      console.error("Error fetching deployment readiness map:", err);
    } finally {
      setLoading(false);
    }
  }, [clientIds]);

  useEffect(() => {
    loadReadinessMap();
  }, [loadReadinessMap]);

  return {
    readinessMap,
    loading,
    error,
    refetch: loadReadinessMap,
  };
}
